import React, { useCallback } from 'react';
import { compose } from '@/utils';

import { DataTable, TableSkeletonRows } from '@/components';

import { useCarriersContext } from './CarriersProvider';

import { ActionMenuList, useCarriersTableColumns } from './CarriersComponent';

import withDialogActions from '@/containers/Dialog/withDialogActions';
import withAlertActions from '@/containers/Alert/withAlertActions';

type Carrier = {
  id: number | string;
  name?: string;
  description?: string;
  url?: string;
  is_active?: boolean;
};

type CarriersContextValue = {
  carriers?: Carrier[];
  isCarriersLoading: boolean;
};

type CarriersDataTableProps = {
  tableProps?: Record<string, unknown>;
  openDialog: (name: string, payload?: Record<string, unknown>) => void;
  openAlert: (name: string, payload?: Record<string, unknown>) => void;
};

function CarriersDataTable({
  // #ownProps
  tableProps,

  // #withDialog.
  openDialog,

  // #withAlertActions
  openAlert,
}: CarriersDataTableProps) {
  const { carriers = [], isCarriersLoading } =
    useCarriersContext() as CarriersContextValue;

  // Table columns.
  const columns = useCarriersTableColumns();

  // Handle edit carrier.
  const handleEditCarrier = useCallback(
    (carrier: Carrier) => {
      openDialog('carrier-form', {
        action: 'edit',
        carrierId: carrier.id,
      });
    },
    [openDialog],
  );

  // Handle delete carrier.
  const handleDeleteCarrier = (carrier: Carrier) => {
    openAlert('carrier-delete', { carrierId: carrier.id });
  };

  return (
    <DataTable
      columns={columns}
      data={carriers}
      loading={isCarriersLoading}
      headerLoading={isCarriersLoading}
      progressBarLoading={isCarriersLoading}
      TableLoadingRenderer={TableSkeletonRows}
      ContextMenu={ActionMenuList}
      noInitialFetch={true}
      payload={{
        onDeleteCarrier: handleDeleteCarrier,
        onEditCarrier: handleEditCarrier,
      }}
      rowContextMenu={ActionMenuList}
      {...tableProps}
    />
  );
}

export default compose(
  withDialogActions,
  withAlertActions,
)(CarriersDataTable);
